import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MaskDirective } from 'ngx-mask';
import { ApiService } from 'src/app/services/api/api.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import utils, { BrValidators } from '../../helpers/utils';

@Component({
  selector: 'app-masked-input',
  templateUrl: './masked-input.component.html',
  styleUrls: ['./masked-input.component.scss'],
})
export class MaskedInputComponent implements OnInit {
  @Input() id?: string = utils.makeID();
  @Input() name?: string;
  @Input() placeholder: string = 'Digite';
  @Input() label?: string;
  @Input() type = 'text';
  @Input() max?: number;

  @Input() standalone: boolean = false;
  @Input() value: string = '';

  // Form control
  @Input() controlName: string = '';
  @Input() group?: any = new FormGroup({});

  @Input() helpText?: string;
  @Input() errorText: string = 'Campo inválido';

  @Input() readOnly: boolean = false;
  @Input() validated: boolean = false;
  @Input() rows: number = 0;

  @Input() isUpperCase = false;

  @Input() isDocument: boolean = false;

  @Input() button: string = '';

  @Input() mask?: string;
  @Input() usePatterns: boolean = false;

  @Input() prefix: string = '';
  @Input() suffix: string = '';

  @Input() hasError: boolean = false;
  @Input() alwaysValid: boolean = false;
  @Input() skipValidation: boolean = false;

  /** Se false, não exibe o asterisco vermelho de obrigatório no label. Default true. */
  @Input() showRequiredIndicator: boolean = true;

  @Output() onButtonClick: EventEmitter<MouseEvent> = new EventEmitter();

  @Output() onChange: EventEmitter<string> = new EventEmitter();

  pattern: MaskDirective['patterns'] = {
    W: {
      pattern: new RegExp('[A-Za-záàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ ]'),
    },
    U: {
      pattern: new RegExp('[A-Z]'),
    },
    L: {
      pattern: new RegExp('[a-z]'),
    },
    N: {
      pattern: new RegExp('[0-9]'),
    },
  };

  error: boolean = false;

  constructor(private apiService: ApiService) {}

  onButtonClicked(event: MouseEvent): void {
    this.onButtonClick.emit(event);
  }

  handleChange(event: any): void {
    this.onChange.emit(event.target.value);
  }

  ngOnInit(): void {
    if (this.isDocument) {
      // Validar se o campo já tem valor ao inicializar
      const initialValue = this.group.controls[this.controlName].value;
      if (initialValue) {
        const documentClean = utils.onlyNumbers(initialValue);
        if (documentClean.length === 11 || documentClean.length === 14) {
          // Usar setTimeout para garantir que o componente está totalmente inicializado
          setTimeout(() => {
            this.documentInUse(documentClean);
          }, 100);
        }
      }

      this.group.controls[this.controlName].valueChanges.subscribe(
        (values: any) => {
          const documentClean = utils.onlyNumbers(values);

          // Se o documento atingiu o tamanho completo (CPF ou CNPJ), validar imediatamente
          if (documentClean.length === 11 || documentClean.length === 14) {
            this.documentInUse(documentClean);
          } else {
            // Se não atingiu o tamanho completo, limpar erros de documento inválido
            const currentErrors = this.group.controls[this.controlName].errors;
            if (
              currentErrors &&
              (currentErrors['invalidDocument'] ||
                currentErrors['documentInUse'])
            ) {
              // Limpar erros se o documento foi alterado e não tem tamanho completo
              if (documentClean.length < 11) {
                this.error = false;
                const errors = { ...currentErrors };
                delete errors['invalidDocument'];
                delete errors['documentInUse'];
                if (Object.keys(errors).length === 0) {
                  this.group.controls[this.controlName].setErrors(null);
                } else {
                  this.group.controls[this.controlName].setErrors(errors);
                }
              }
            }
          }
        }
      );
    }
  }

  getClasses(): string {
    if (this.skipValidation) {
      return '';
    }

    if (this.error) {
      return 'error';
    }

    if (this.validated) {
      return 'validated';
    }

    return '';
  }

  getRequired(): boolean {
    const control = this.group.controls[this.controlName];

    if (control && control.validator) {
      let required = false;

      const validator = control.validator({} as AbstractControl);

      if (validator && validator.required) {
        required = true;
      }

      return required;
    }

    return false;
  }

  getTouched(): boolean {
    if (this.standalone) {
      return true;
    } else {
      let touched = false;

      if (this.readOnly) return touched;

      if (this.group) {
        const control = this.group.controls[this.controlName];

        if (control) {
          if (control.touched) {
            touched = true;
          }
        } else {
          console.error(`Control of ${this.controlName} not found!`);
        }
      }

      return touched;
    }
  }

  getInvalid(): boolean {
    if (this.skipValidation) {
      return false;
    }

    if (this.standalone) {
      return this.hasError;
    } else {
      let invalid = false;

      if (this.readOnly) return invalid;

      if (this.group) {
        const control = this.group.controls[this.controlName];

        if (control.invalid) {
          invalid = true;
        }
      }

      return invalid;
    }
  }

  async documentInUse(document: string) {
    if (!document || document.length === 0) {
      return;
    }

    // Determinar se é CPF ou CNPJ baseado no tamanho do documento
    const documentClean = document.replace(/[^\d]+/g, '');
    const isCpf = documentClean.length === 11;
    const isCnpj = documentClean.length === 14;

    // Validar CPF ou CNPJ antes de chamar a API
    let isValid = false;
    let validationError = '';

    if (isCpf) {
      isValid = BrValidators.cpf(document);
      if (!isValid) {
        validationError = 'CPF inválido';
      }
    } else if (isCnpj) {
      isValid = BrValidators.cnpj(document);
      if (!isValid) {
        validationError = 'CNPJ inválido';
      }
    } else {
      // Se não tem 11 ou 14 dígitos, não valida ainda (pode estar digitando)
      return;
    }

    // Se o documento não é válido, mostrar erro e não chamar a API
    if (!isValid && validationError) {
      this.errorText = validationError;
      this.error = true;
      this.group.controls[this.controlName].setErrors({
        invalidDocument: true,
      });
      return;
    }

    // Se o documento é válido, então chamar a API para verificar se já existe
    try {
      const res = await this.apiService.post<ValidateFieldResponse>({
        route: 'validate-document-exists/',
        body: { document: document },
      });

      if (res.exists) {
        this.errorText = 'Este documento já está cadastrado';
        this.error = true;
        this.group.controls[this.controlName].setErrors({
          documentInUse: true,
        });
      } else {
        // Remove apenas o erro de documentInUse, mantendo outros erros
        const currentErrors = this.group.controls[this.controlName].errors;
        if (currentErrors && currentErrors['documentInUse']) {
          delete currentErrors['documentInUse'];
          if (Object.keys(currentErrors).length === 0) {
            this.group.controls[this.controlName].setErrors(null);
          } else {
            this.group.controls[this.controlName].setErrors(currentErrors);
          }
        }
        // Limpar erro de documento inválido se existir
        if (currentErrors && currentErrors['invalidDocument']) {
          delete currentErrors['invalidDocument'];
          if (Object.keys(currentErrors).length === 0) {
            this.group.controls[this.controlName].setErrors(null);
          } else {
            this.group.controls[this.controlName].setErrors(currentErrors);
          }
        }
        this.error = false;
      }
    } catch (error) {
      this.error = false;
      // Não limpar erros de validação em caso de erro na API
      const currentErrors = this.group.controls[this.controlName].errors;
      if (currentErrors && currentErrors['invalidDocument']) {
        // Manter erro de documento inválido
        return;
      }
      this.group.controls[this.controlName].setErrors(null);
    }
  }

  getError(): boolean {
    if (this.alwaysValid || this.skipValidation) return false;
    
    // Para campos standalone, verifica diretamente se há erro
    if (this.standalone) {
      return this.getInvalid();
    }
    
    const control = this.group?.controls[this.controlName];
    // Mostra erro se o campo foi alterado (dirty) e está inválido
    return control && (control.dirty || control.touched) && this.getInvalid();
  }

  getErrorMessage(): string {
    if (this.standalone) {
      return this.errorText;
    }

    if (!this.group) {
      return this.errorText;
    }

    const control = this.group.controls[this.controlName];
    if (!control || !control.errors) {
      return this.errorText;
    }

    const errors = control.errors;

    // Verifica erros específicos primeiro
    // Validações de documento
    if (errors['invalidDocument']) {
      return this.errorText || 'Documento inválido';
    }
    if (errors['documentInUse']) {
      return this.errorText || 'Este documento já está cadastrado';
    }
    // Validações de nome completo
    if (errors['fullNameIncomplete']) {
      return 'O nome deve estar completo';
    }
    if (errors['fullNameTitle']) {
      return 'Não é permitido usar títulos ou saudações no nome';
    }
    if (errors['fullNameAbbreviated']) {
      return 'Não é permitido usar abreviações no nome';
    }
    if (errors['fullNameNumbers']) {
      return 'O nome não pode conter números';
    }
    if (errors['fullName']) {
      return 'Nome completo inválido';
    }

    // Validações de documento
    if (errors['invalidDocument']) {
      return this.errorText || 'Documento inválido';
    }

    if (errors['documentInUse']) {
      return this.errorText || 'Este documento já está cadastrado';
    }

    if (errors['cpf']) {
      return 'CPF inválido';
    }

    if (errors['cnpj']) {
      return 'CNPJ inválido';
    }

    if (errors['invalid']) {
      return 'Documento inválido';
    }

    // Validações de email
    if (errors['email']) {
      return 'Email inválido';
    }

    // Validações de telefone/celular
    if (errors['telefone'] || errors['celular'] || errors['telefoneFixo']) {
      return 'Telefone inválido';
    }

    // Validações específicas de celular (HEAD)
    if (errors['celularDDIDuplicated']) {
      return 'O número não pode conter DDI duplicado';
    }
    if (errors['celularRepeated']) {
      return 'Número de celular inválido';
    }

    // Validações de telefone (versão incoming)
    if (errors['invalidDDI']) {
      return 'Número inválido';
    }

    if (errors['invalidPhoneLength']) {
      return 'Número de telefone inválido';
    }

    if (errors['invalidPhonePattern']) {
      return 'Número de telefone inválido';
    }

    // Validações de CEP
    if (errors['cep']) {
      return 'CEP inválido';
    }

    // Validações de idade (HEAD)
    if (errors['age']) {
      return 'É necessário ser maior de 18 anos';
    }

    // Validações de data
    if (errors['date']) {
      return 'Data inválida';
    }

    if (errors['required']) {
      return 'Campo obrigatório';
    }

    // Erro de data maior que hoje (para campos como foundation_date)
    if (errors['max']) {
      if (this.controlName === 'foundation_date') {
        return 'A data da fundação não pode ser uma data futura';
      }
      if (this.controlName === 'max_term_validity') {
        return 'A validade do mandato não pode ultrapassar 100 anos a partir de hoje';
      }
      return 'A data não pode ser futura';
    }

    // Validação de data mínima em relação à data de constituição
    if (errors['minConstitutionDate']) {
      return 'A data não pode ser menor que a data de constituição do fundo';
    }

    // Erro de valor mínimo
    if (errors['min']) {
      // Se há errorText customizado, usa ele (para campos numéricos)
      if (this.errorText && this.errorText !== 'Campo inválido') {
        return this.errorText;
      }
      // Se não, assume que é data
      return 'Data inválida';
    }

    if (errors['minlength']) {
      return `Mínimo de ${errors['minlength'].requiredLength} caracteres`;
    }

    if (errors['maxlength']) {
      return `Máximo de ${errors['maxlength'].requiredLength} caracteres`;
    }

    if (errors['pattern']) {
      if (this.isDocument) {
        return 'Documento inválido';
      }
      return 'Formato inválido';
    }

    if (errors['mask']) {
      if (this.isDocument) {
        return 'Documento inválido';
      }
      return 'Formato inválido';
    }

    if (errors['cepIncomplete']) {
      return 'CEP incompleto';
    }

    return this.errorText;
  }
}
